import { applyMiddleware, compose, createStore } from 'redux'
import { createEpicMiddleware } from 'redux-observable'
import thunk from 'redux-thunk'

import {
  selectActualCompanyId,
  selectSelfCompanyId,
  selectSelfIsPartner,
  selectUserEmail,
  selectUserId,
} from './containers/App/selectors'
import { ASYNC_ERROR_POSTFIX } from './utils/ActionCreator'
import ls from './utils/LocalStorage'
import { pendoMSPSupportMiddleware } from './utils/pendo'
import S from './utils/sentences'
import { URL_CONST } from './utils/urlConsts'
import api from './api'
import createEpics, { epicsErrorHandler } from './epics'
import createReducer from './reducer'

import * as Sentry from '@sentry/browser'

export default () => {
  ls.init()

  const epicMiddleware = createEpicMiddleware(createEpics(), {
    dependencies: {
      ls,
      api,
      epicsErrorHandler,
    },
  })

  const requestErrorHandler = (store) => (next) => (action) => {
    if (
      action.error &&
      action.type.indexOf(ASYNC_ERROR_POSTFIX) !== -1 // action type is async error
    ) {
      if (action.error.status === 401) {
        // user unauthorized
        let url = URL_CONST.login.signin
        const message = action.error.response?.message || S.SESSION_EXPIRED
        // show preloader from new-ui.html
        if (typeof window.showPreloader === 'function') window.showPreloader()
        if (message) url += `?error=${message}`
        window.location = url
      } else if (
        window.SENTRY_INITIALIZED &&
        Number(action.error.status) !== 0 && // request is not canceled; nginx sees it as 499
        !action.error.status >= 500 && // we assume be is checking their logs
        !action.error.expectedErrorCodes?.includes(action.error.status) // status code is not handled
      ) {
        const state = store.getState()
        Sentry.captureException(new Error(`[${action.error.status}] ${action.type}`), {
          user: {
            id: selectUserId(state),
            email: selectUserEmail(state),
          },
          extra: {
            action,
            response: action.error.response, // display response on the first level
          },
          tags: {
            source: 'error during async action',
            statusCode: action.error.status,

            companyId: selectSelfCompanyId(state),
            msp: selectSelfIsPartner(state),
            selectedCompanyId: selectActualCompanyId(state),
          },
          // Events grouping rules
          fingerprint: [action.error.status, action.type],
        })
      }
    }
    return next(action)
  }

  const middleware = [
    epicMiddleware,
    thunk.withExtraArgument({ api }),
    requestErrorHandler,
    pendoMSPSupportMiddleware,
  ]

  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ // eslint-disable-line
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        // eslint-disable-line
        shouldHotReload: false,
        trace: true,
        actionsDenylist: ['INCIDENTS_COUNT_POLLING_ASYNC'],
      })
    : compose

  const enhancer = composeEnhancers(applyMiddleware(...middleware))

  const store = createStore(createReducer(), enhancer)

  return store
}
